/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useStateCallback, safeInvoke, isDefined, isFunction, useMergedRef } from '@sravni/react-utils';
import { Cross, ArrowDown } from '@sravni/react-icons';
import '../../Typography/index.js';
import '../../Textarea/index.js';
import '../../Dropdown/index.js';
import '../../Space/index.js';
import { Menu } from '../../Menu/index.js';
import modules_b8cc600d from '../styles/Autocomplete.module.less.js';
import { Text } from '../../Typography/src/Text.js';
import Space from '../../Space/src/Space.js';
import { Heading } from '../../Typography/src/Heading.js';
import Textarea from '../../Textarea/src/Textarea.js';
import Dropdown from '../../Dropdown/src/Dropdown.js';

var defaultNotFoundText = 'К сожалению, по вашим параметрам ничего не найдено, попробуйте изменить их';
var MIN_ROWS = 1;
/**
 * @description The Autocomplete component allow user to type and select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Autocomplete placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
var Autocomplete = React.forwardRef(function (props, ref) {
    var defaultValue = props.defaultValue, value = props.value, options = props.options, autoFocus = props.autoFocus, invalid = props.invalid, disabled = props.disabled, className = props.className, menuClassName = props.menuClassName, mode = props.mode, icon = props.icon, readOnly = props.readOnly, placement = props.placement, fixedWidth = props.fixedWidth, resizable = props.resizable, notFoundContent = props.notFoundContent, title = props.title, _a = props.closeOnSelect, closeOnSelect = _a === void 0 ? true : _a, _b = props.flip, flip = _b === void 0 ? true : _b, onChange = props.onChange, onSearch = props.onSearch, onClear = props.onClear, onFocus = props.onFocus, onBlur = props.onBlur, restProps = __rest(props
    // TODO мобильный вид требует доработки
    , ["defaultValue", "value", "options", "autoFocus", "invalid", "disabled", "className", "menuClassName", "mode", "icon", "readOnly", "placement", "fixedWidth", "resizable", "notFoundContent", "title", "closeOnSelect", "flip", "onChange", "onSearch", "onClear", "onFocus", "onBlur"]);
    // TODO мобильный вид требует доработки
    var mobile = false;
    var cx = useStyles(modules_b8cc600d);
    var classNames = cx(className, 'container');
    var containerRef = React.useRef(null);
    var inputRef = React.useRef(null);
    var menuRef = React.useRef(null);
    var hasUserInput = React.useRef(false);
    var formControlContext = useFormControlContext();
    var _c = __read(useBoolean(false), 2), visible = _c[0], setVisible = _c[1];
    var _d = __read(useBoolean(false), 2), focused = _d[0], setFocused = _d[1];
    var _e = __read(useBoolean(fixedWidth), 2), fixWidth = _e[0], setFixWidth = _e[1];
    var _f = __read(useStateCallback(value !== null && value !== void 0 ? value : defaultValue), 2), innerValue = _f[0], setInnerValue = _f[1];
    var _g = __read(React.useState(''), 2), inputValue = _g[0], setInputValue = _g[1];
    var _h = __read(React.useState(options || []), 2), innerOptions = _h[0], setInnerOptions = _h[1];
    var _j = __read(React.useState(false), 2), selected = _j[0], setSelected = _j[1];
    var isMultiple = React.useMemo(function () { return mode === 'multiple'; }, [mode]);
    var menuClassNames = React.useMemo(function () {
        return cx(menuClassName, {
            hideMenu: !hasUserInput.current && !innerOptions.length,
        });
    }, [innerOptions, menuClassName]);
    var optionsMap = React.useMemo(function () {
        if (!options) {
            return {};
        }
        return options.reduce(function (acc, o) {
            acc[o.value] = o;
            return acc;
        }, {});
    }, [options]);
    var getOptionByValue = React.useCallback(function (value) {
        return optionsMap[value] || {};
    }, [optionsMap]);
    var displayValue = React.useMemo(function () {
        if (isMultiple) {
            if (visible && !selected)
                return inputValue;
            if (!Array.isArray(innerValue) || !innerValue.length)
                return '';
            return innerValue
                .map(function (v) { var _a; return (_a = getOptionByValue(v)) === null || _a === void 0 ? void 0 : _a.label; })
                .filter(Boolean)
                .join(', ');
        }
        if (visible) {
            return inputValue;
        }
        var label = getOptionByValue(innerValue).label;
        return label;
    }, [isMultiple, selected, visible, inputValue, innerValue, getOptionByValue]);
    var setValue = React.useCallback(function (value) {
        setInnerValue(value, function (val) {
            if (!isMultiple) {
                var label = getOptionByValue(val).label;
                setInputValue(label ? label : val);
            }
            safeInvoke(onChange, val !== null && val !== void 0 ? val : '');
        });
    }, [onChange]);
    var closeMenuOnSelect = React.useCallback(function (value) { return (isDefined(closeOnSelect) && isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect); }, [closeOnSelect]);
    var handleSelect = React.useCallback(function (value) {
        setValue(value);
        isMultiple && setSelected(true);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [isMultiple, closeMenuOnSelect, setValue]);
    var handleClick = React.useCallback(function () {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var handleChangeInput = React.useCallback(function (event) {
        var _a;
        var value = event.currentTarget.value;
        setInputValue(value);
        if (onSearch === undefined) {
            setInnerOptions(options.filter(function (o) {
                return o.label.toLowerCase().startsWith(value.toLowerCase());
            }));
        }
        hasUserInput.current = true;
        safeInvoke(onSearch, value);
        !visible && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click());
    }, [options, visible, onSearch]);
    var emptyContent = React.useMemo(function () {
        setFixWidth.on();
        return (React.createElement(Menu, { onClick: function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }, notFoundContent ? (notFoundContent) : (React.createElement(Menu.Item, { disabled: true },
            React.createElement(Text, { className: "h-text-center h-color-D60" }, defaultNotFoundText)))));
    }, [notFoundContent]);
    var handleFocusInput = React.useCallback(function (event) {
        if (!focused) {
            var label = getOptionByValue(innerValue).label;
            setFocused.on();
            setInputValue(label);
            safeInvoke(onFocus, event);
        }
    }, [focused, innerValue, getOptionByValue, onFocus]);
    var handleBlurInput = React.useCallback(function (event) {
        if (menuRef.current && menuRef.current.contains(event.relatedTarget)) {
            return;
        }
        if (!visible && containerRef.current && containerRef.current.contains(event.target)) {
            return;
        }
        safeInvoke(onBlur, event);
        setVisible.off();
        setFocused.off();
    }, [visible, onBlur]);
    var handleClickClear = React.useCallback(function (event) {
        event.stopPropagation();
        setValue('');
        safeInvoke(onClear);
    }, [setValue, onClear]);
    var handleClose = React.useCallback(function () {
        var _a;
        setVisible.off();
        mobile && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur());
    }, [mobile, handleBlurInput]);
    var iconElement = React.useMemo(function () {
        var _a;
        if (visible && isDefined(innerValue) && innerValue !== '') {
            return React.createElement(Cross, { className: cx('icon'), onClick: handleClickClear });
        }
        if (icon || icon === null) {
            return icon;
        }
        return (React.createElement(ArrowDown, { className: cx('icon', (_a = {},
                _a["isArrowTurned"] = visible,
                _a)) }));
    }, [visible, innerValue, icon, handleClickClear]);
    var header = React.useMemo(function () {
        if (!mobile) {
            return null;
        }
        return (React.createElement(Space, { direction: "vertical", size: 16 },
            !!title && React.createElement(Heading, { level: 3 }, title),
            React.createElement(Textarea
            //autoFocus
            , { 
                //autoFocus
                value: displayValue, icon: iconElement, resizable: resizable, rows: MIN_ROWS, wrap: resizable ? 'soft' : 'off', onChange: handleChangeInput })));
    }, [mobile, title, displayValue, iconElement, resizable, handleChangeInput]);
    var classNamesTextarea = cx('textarea', {
        'with-icon': iconElement && !resizable,
        focus: focused || visible,
    });
    //defaultValue needs only on first render
    React.useEffect(function () {
        var _a;
        if (visible) {
            return;
        }
        if (isDefined(value !== null && value !== void 0 ? value : defaultValue)) {
            var label = getOptionByValue((_a = value) !== null && _a !== void 0 ? _a : defaultValue).label;
            setInputValue(label);
        }
        if (isDefined(value)) {
            setInnerValue(value);
        }
    }, [value, visible, getOptionByValue]);
    React.useEffect(function () {
        var _a;
        autoFocus && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus());
    }, [autoFocus]);
    React.useEffect(function () {
        if (visible) {
            !hasUserInput.current && isMultiple && setInputValue('');
        }
        else {
            setInnerOptions(options || []);
            hasUserInput.current = false;
        }
    }, [visible, isMultiple, options]);
    React.useEffect(function () {
        setInnerOptions(options || []);
    }, [options]);
    React.useEffect(function () {
        !visible && setSelected(false);
    }, [visible]);
    return (React.createElement(Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, className: menuClassNames, mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixWidth, mobile: mobile, header: header, closeOnSelect: closeOnSelect, flip: flip, emptyContent: hasUserInput.current ? emptyContent : null, tabIndex: -1, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect, onClick: handleClick },
        React.createElement("div", { ref: containerRef, "data-qa": "Autocomplete", className: classNames, tabIndex: -1 },
            React.createElement(Textarea, __assign({}, restProps, { ref: useMergedRef(ref, inputRef), className: classNamesTextarea, readOnly: readOnly || mobile || selected || (!visible && isMultiple), invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, resizable: resizable, rows: MIN_ROWS, wrap: resizable ? 'soft' : 'off', onChange: handleChangeInput, onFocus: handleFocusInput, onBlur: handleBlurInput })))));
});
Autocomplete.defaultProps = {
    fixedWidth: true,
    placement: 'bottom-start',
    resizable: false,
    closeOnSelect: true,
};
Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
export { defaultNotFoundText };
//# sourceMappingURL=Autocomplete.js.map
